/**
 * Extrato da aba GERENCIAL para a Capacidade da Fábrica, lido PELO TEXTO DO
 * CABEÇALHO (linha 4), não pela letra da coluna.
 *
 * Por que não reaproveitar `processGerencial`: ele lê por letra fixa do
 * layout de 2026 e, com `sheet_to_json(raw)`, transforma `#VALUE!`/`#N/A`
 * em vazio sem aviso. O layout de 2025 é deslocado a partir de BO (a
 * marcenaria cai em BT–BV e o carregamento em CL), então as cópias antigas,
 * necessárias para o histórico do ritmo, sairiam desalinhadas sem ninguém
 * perceber. Aqui o cabeçalho obrigatório ausente ou repetido BLOQUEIA, e a
 * célula de erro do Excel é registrada em `erros`.
 *
 * Função pura sobre a planilha SheetJS: o `XLSX` é injetado para este módulo
 * não puxar a biblioteca para o bundle de quem só usa o domínio.
 */
import { chaveAmbiente } from "../../pages/MapaProducao/dados/chaves";
import { isYes, normalizeText } from "../data";
import { F } from "../gerencialFields";
import { cellToString, ehErroExcel } from "../xlsxHelpers";
import { diaParaIso, isoParaDia } from "./calendario";
import { ehCancelado, ehServico, terminoEfetivo } from "./normalizacao";

/** Versão das regras do leitor: entra na chave idempotente da foto. */
export const VERSAO_LEITOR = 1;
export const MOTIVO_LAYOUT = "layout_nao_reconhecido";

const INDICE_CABECALHO = 3; // linha 4 do Excel
const INDICE_PRIMEIRA_LINHA = 4; // linha 5 do Excel
const CABECALHO_PERFIL_2026 = "RECEBIMENTO TERCEIROS";

const TEXTO = "texto";
const DATA = "data";
const INTEIRO = "inteiro";

const COLUNAS = [
  { campo: "pedido", cabecalho: "NUM. PEDIDO", obrigatorio: true, tipo: TEXTO },
  { campo: "data_venda", cabecalho: "DATA VENDA", obrigatorio: true, tipo: DATA },
  { campo: "obra_futura", cabecalho: "OBRA FUTURA", obrigatorio: true, tipo: TEXTO },
  // Coluna N: "ENTREGA PREVISTA" desde 08/10/2026; as cópias antigas (o
  // histórico do ritmo) trazem o nome anterior. Mesmo conteúdo, conferido
  // linha a linha entre as duas versões da planilha.
  {
    campo: "entrega_contrato",
    cabecalho: "ENTREGA PREVISTA",
    outrosNomes: ["ENTREGA PROGRAMADA CONTRATO"],
    obrigatorio: true,
    tipo: DATA,
  },
  { campo: "aprovacao", cabecalho: "APROVACAO", obrigatorio: true, tipo: DATA },
  { campo: "cliente", cabecalho: "CLIENTE", obrigatorio: true, tipo: TEXTO },
  { campo: "ambiente", cabecalho: "AMBIENTE", obrigatorio: true, tipo: TEXTO },
  { campo: "lote", cabecalho: "NUM. LOTE", obrigatorio: true, tipo: TEXTO },
  { campo: "corte", cabecalho: "CORTE", obrigatorio: false, tipo: DATA },
  { campo: "marceneiro", cabecalho: "MARCENEIRO RESPONSAVEL", obrigatorio: false, tipo: TEXTO },
  { campo: "marcenaria_inicio", cabecalho: "MARCENARIA INICIO", obrigatorio: true, tipo: DATA },
  { campo: "marcenaria_termino", cabecalho: "MARCENARIA TERMINO", obrigatorio: true, tipo: DATA },
  { campo: "carregamento", cabecalho: "CARREGAMENTO", obrigatorio: true, tipo: DATA },
  { campo: "data_reprogramada", cabecalho: "DATA REPROGRAMADA", obrigatorio: true, tipo: DATA },
  // Só para o "o que digitar" do Cenário do PCP (F2): ficam no extrato, mas
  // NÃO vão na foto (o contrato da LINHA não muda). CO é a fórmula
  // WORKDAY(APROVAÇÃO, PRAZO) e CR = WORKDAY(CO, CQ).
  { campo: "entrega_cliente", cabecalho: "ENTREGA CLIENTE", obrigatorio: false, tipo: DATA, local: true },
  { campo: "dias_reprogramado", cabecalho: "DIAS REPROGRAMADO", obrigatorio: false, tipo: INTEIRO, local: true },
];

/** Campos de data cuja célula pode ser erro do Excel (contrato de `erros`). */
export const CAMPOS_DE_DATA = COLUNAS.filter((c) => c.tipo === DATA && !c.local).map((c) => c.campo);

// Marcadores que a planilha usa para "não se aplica / não feito".
const MARCADORES_SEM_DATA = new Set(["", ".", "X", "-"]);
const PADRAO_DATA_BR = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;

const MS_POR_DIA = 86400000;
// Serial 0 do Excel (sistema 1900) = 1899-12-30.
const DIA_DO_SERIAL_ZERO = Math.round(Date.UTC(1899, 11, 30) / MS_POR_DIA);
// Tolerância para "a data é meia-noite": absorve os segundos do fuso
// histórico (LMT de São Paulo em 1899 = −3h06m28s) que o getTimezoneOffset
// trunca para minutos. Pequena de propósito: uma célula com hora de verdade
// (23h30) não pode virar o dia seguinte.
const TOLERANCIA_MEIA_NOITE = 1 / 1440;

// Maior serial que o Excel aceita (9999-12-31). Acima disso é número
// digitado numa coluna de data (24102025 sem as barras), não data: viraria
// um ano de 5 dígitos, que o backend recusa (derrubando a foto inteira) e o
// calendário do domínio não sabe somar.
const SERIAL_MAXIMO_EXCEL = 2958465;

function serialForaDoExcel(serial) {
  return Number.isFinite(serial) && Math.floor(serial) > SERIAL_MAXIMO_EXCEL;
}

function serialParaIso(serial) {
  if (!Number.isFinite(serial) || serial < 1) return null; // 0 = "data zero" do INDEX/MATCH vazio
  if (serialForaDoExcel(serial)) return null;
  const inteiro = Math.round(serial);
  const dias = Math.abs(serial - inteiro) < TOLERANCIA_MEIA_NOITE ? inteiro : Math.floor(serial);
  return diaParaIso(DIA_DO_SERIAL_ZERO + dias);
}

// Desfaz a conta da SheetJS (cellDates): Date local → serial do Excel.
function serialDaData(data) {
  const base = new Date(1899, 11, 30, 0, 0, 0);
  const limiar = base.getTime() + (data.getTimezoneOffset() - base.getTimezoneOffset()) * 60000;
  return (data.getTime() - limiar) / MS_POR_DIA;
}

/**
 * Date de célula → "AAAA-MM-DD". A SheetJS (cellDates) monta a Date a partir
 * do serial com a meia-noite LOCAL de 1899-12-30 e corrige o fuso; aqui o
 * caminho é desfeito com a mesma conta (`datenum` da própria SheetJS) e o
 * serial vira ISO por aritmética pura. Ler `getUTCDate()` direto erraria um
 * dia em fuso a leste de UTC; ler `getDate()` erraria com hora ≥ 21h em
 * fuso a oeste.
 */
export function isoDeDataDaPlanilha(data) {
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) return null;
  return serialParaIso(serialDaData(data));
}

// Número além do último dia do Excel é texto mal digitado: null + problema
// "texto" (vai para `erros_texto` e aparece na aba Qualidade).
function lerSerial(serial) {
  if (serialForaDoExcel(serial)) return { valor: null, problema: "texto" };
  return { valor: serialParaIso(serial), problema: null };
}

function isoDeTextoBr(texto) {
  const partes = PADRAO_DATA_BR.exec(texto);
  if (!partes) return null;
  const ano = partes[3].length === 2 ? 2000 + Number(partes[3]) : Number(partes[3]);
  const iso = `${ano}-${partes[2].padStart(2, "0")}-${partes[1].padStart(2, "0")}`;
  return isoParaDia(iso) === null ? null : iso;
}

// { valor, problema } — problema: "erro" (célula de erro do Excel) ou
// "texto" (texto que não é data nem marcador conhecido).
function lerData(celula) {
  if (!celula || celula.v === null || celula.v === undefined) return { valor: null, problema: null };
  if (celula.t === "e") return { valor: null, problema: "erro" };
  const { v } = celula;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? { valor: null, problema: null } : lerSerial(serialDaData(v));
  }
  if (typeof v === "number") return lerSerial(v);
  if (typeof v !== "string") return { valor: null, problema: "texto" };
  const texto = v.trim();
  if (MARCADORES_SEM_DATA.has(texto.toUpperCase())) return { valor: null, problema: null };
  if (ehErroExcel(texto)) return { valor: null, problema: "erro" };
  const iso = isoDeTextoBr(texto);
  return iso ? { valor: iso, problema: null } : { valor: null, problema: "texto" };
}

// O backend aceita strings de até 300 caracteres em cada campo da LINHA.
const TAMANHO_MAXIMO_TEXTO = 300;

function lerTexto(celula) {
  if (!celula || celula.t === "e") return null;
  const texto = cellToString(celula.v);
  return texto ? texto.slice(0, TAMANHO_MAXIMO_TEXTO) : null;
}

const PADRAO_INTEIRO = /^[+-]?\d+$/;

// { valor, problema }. O Excel trunca o número de dias do WORKDAY; aqui
// também (Math.trunc), para "o que digitar" bater com o que ele calcula.
// Coluna formatada como data por engano chega como Date: volta ao serial.
function lerInteiro(celula) {
  if (!celula || celula.v === null || celula.v === undefined) return { valor: null, problema: null };
  if (celula.t === "e") return { valor: null, problema: "erro" };
  const { v } = celula;
  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? { valor: null, problema: null } : { valor: Math.round(serialDaData(v)), problema: null };
  }
  if (typeof v === "number") return Number.isFinite(v) ? { valor: Math.trunc(v), problema: null } : { valor: null, problema: "texto" };
  const texto = String(v).trim();
  if (MARCADORES_SEM_DATA.has(texto.toUpperCase())) return { valor: null, problema: null };
  if (PADRAO_INTEIRO.test(texto)) return { valor: Number(texto), problema: null };
  return { valor: null, problema: ehErroExcel(texto) ? "erro" : "texto" };
}

function textoDoCabecalho(celula) {
  if (!celula || celula.t === "e") return "";
  return normalizeText(cellToString(celula.v));
}

/** Mapa cabeçalho normalizado → índices de coluna onde ele aparece. */
function lerCabecalho(ws, XLSX, intervalo) {
  const indices = new Map();
  for (let c = intervalo.s.c; c <= intervalo.e.c; c += 1) {
    const texto = textoDoCabecalho(ws[XLSX.utils.encode_cell({ r: INDICE_CABECALHO, c })]);
    if (!texto) continue;
    indices.set(texto, [...(indices.get(texto) || []), c]);
  }
  return indices;
}

function resolverColunas(cabecalho) {
  const faltando = [];
  const repetidos = [];
  const colunas = [];
  COLUNAS.forEach((coluna) => {
    const nomes = [coluna.cabecalho, ...(coluna.outrosNomes || [])];
    const encontrados = nomes.flatMap((nome) => cabecalho.get(nome) || []);
    if (encontrados.length === 1) {
      colunas.push({ ...coluna, indice: encontrados[0] });
      return;
    }
    // Opcional repetido também fica de fora: ler a coluna errada é pior
    // do que não ler (o campo sai null em todas as linhas).
    if (encontrados.length > 1) repetidos.push(coluna.cabecalho);
    if (coluna.obrigatorio) faltando.push(coluna.cabecalho);
  });
  return { colunas, faltando, repetidos };
}

function identidadeDe(chave, dataVenda) {
  return chave ? `${chave}|${dataVenda || ""}` : null;
}

function montarLinha(bruta, numeroLinhaExcel) {
  const chave = chaveAmbiente({ [F.NUMERO_DO_PEDIDO]: bruta.pedido, [F.AMBIENTE]: bruta.ambiente });
  const { termino, ajuste_termino: ajusteTermino } = terminoEfetivo(bruta);
  return {
    identidade: identidadeDe(chave, bruta.data_venda),
    chave,
    pedido: bruta.pedido,
    ambiente: bruta.ambiente,
    cliente: bruta.cliente,
    linha_planilha: numeroLinhaExcel,
    data_venda: bruta.data_venda,
    obra_futura: isYes(bruta.obra_futura),
    entrega_contrato: bruta.entrega_contrato,
    aprovacao: bruta.aprovacao,
    data_reprogramada: bruta.data_reprogramada,
    entrega_cliente: bruta.entrega_cliente,
    dias_reprogramado: bruta.dias_reprogramado,
    corte: bruta.corte,
    marcenaria_inicio: bruta.marcenaria_inicio,
    marcenaria_termino: bruta.marcenaria_termino,
    carregamento: bruta.carregamento,
    lote: bruta.lote,
    marceneiro: bruta.marceneiro,
    termino,
    ajuste_termino: ajusteTermino,
    servico: ehServico(bruta),
    cancelado: ehCancelado(bruta),
    erros: bruta.erros,
    // Só para a aba Qualidade: não faz parte da LINHA enviada ao backend.
    erros_texto: bruta.erros_texto,
    // Problemas nas colunas locais (CO, CQ): fora de `erros`, cujo contrato
    // com o backend só aceita os campos da foto.
    erros_locais: bruta.erros_locais,
  };
}

function lerLinha(ws, XLSX, r, colunas) {
  const bruta = { erros: [], erros_texto: [], erros_locais: [] };
  COLUNAS.forEach((coluna) => {
    bruta[coluna.campo] = null;
  });
  colunas.forEach((coluna) => {
    const celula = ws[XLSX.utils.encode_cell({ r, c: coluna.indice })];
    if (coluna.tipo === TEXTO) {
      bruta[coluna.campo] = lerTexto(celula);
      return;
    }
    const { valor, problema } = coluna.tipo === INTEIRO ? lerInteiro(celula) : lerData(celula);
    bruta[coluna.campo] = valor;
    if (!problema) return;
    if (coluna.local) bruta.erros_locais.push(coluna.campo);
    else if (problema === "erro") bruta.erros.push(coluna.campo);
    else bruta.erros_texto.push(coluna.campo);
  });
  return bruta;
}

const INDICE_PRIMEIRO_FERIADO = 1; // TABELAS!A2

function celulaVazia(celula) {
  if (!celula || celula.v === null || celula.v === undefined) return true;
  return typeof celula.v === "string" && celula.v.trim() === "";
}

/**
 * Feriados da aba TABELAS (coluna A a partir da linha 2, até a primeira
 * célula vazia depois de pelo menos uma data): é o calendário que o Excel
 * usa no WORKDAY de CO e CR. Ele NÃO é o calendário da unidade (a aba ficou
 * sem os feriados de 2026); serve só para dizer ao PCP o número que o Excel
 * vai entender. `null` quando a aba não existe.
 */
export function lerFeriadosDaPlanilha(wsTabelas, XLSX) {
  if (!wsTabelas || !wsTabelas["!ref"]) return null;
  const intervalo = XLSX.utils.decode_range(wsTabelas["!ref"]);
  const datas = new Set();
  for (let r = INDICE_PRIMEIRO_FERIADO; r <= intervalo.e.r; r += 1) {
    const celula = wsTabelas[XLSX.utils.encode_cell({ r, c: 0 })];
    if (celulaVazia(celula)) {
      if (datas.size > 0) break;
      continue;
    }
    const { valor } = lerData(celula);
    if (valor) datas.add(valor);
  }
  return [...datas].sort();
}

/**
 * `{ok:true, perfil:"2025"|"2026", linhas:[LINHA], versaoLeitor,
 * colunasIgnoradas, feriadosPlanilha, tabelasAusente}` ou
 * `{ok:false, motivo:"layout_nao_reconhecido", faltando:[...], repetidos:[...]}`.
 * Só entram linhas com CLIENTE preenchido (mesma regra do parser atual).
 * `wsTabelas` é a aba TABELAS (opcional; ausente → `tabelasAusente`).
 */
export function extrairLinhasCapacidade(ws, XLSX, wsTabelas) {
  if (!ws || !ws["!ref"]) {
    return { ok: false, motivo: MOTIVO_LAYOUT, faltando: COLUNAS.filter((c) => c.obrigatorio).map((c) => c.cabecalho), repetidos: [] };
  }
  const intervalo = XLSX.utils.decode_range(ws["!ref"]);
  const cabecalho = lerCabecalho(ws, XLSX, intervalo);
  const { colunas, faltando, repetidos } = resolverColunas(cabecalho);
  if (faltando.length > 0) {
    return { ok: false, motivo: MOTIVO_LAYOUT, faltando, repetidos };
  }

  const feriados = lerFeriadosDaPlanilha(wsTabelas, XLSX);
  const linhas = [];
  for (let r = INDICE_PRIMEIRA_LINHA; r <= intervalo.e.r; r += 1) {
    const bruta = lerLinha(ws, XLSX, r, colunas);
    if (!bruta.cliente) continue;
    linhas.push(montarLinha(bruta, r + 1));
  }
  return {
    ok: true,
    perfil: cabecalho.has(CABECALHO_PERFIL_2026) ? "2026" : "2025",
    linhas,
    versaoLeitor: VERSAO_LEITOR,
    // Opcional repetido: o campo saiu vazio em todas as linhas; a tela avisa.
    colunasIgnoradas: repetidos,
    feriadosPlanilha: feriados || [],
    tabelasAusente: feriados === null,
  };
}
